export const CHOOSER_OTHER_INPUT_KEY = '__chooser_other_input__';

export function resolveSingleCustomInputChoiceKey(page = null) {
  const pageRecord = Object.assign({}, page);
  const options = Array.isArray(pageRecord.options) ? pageRecord.options : [];
  if (options.length !== 1) {
    return '';
  }
  const choice = options[0] || {};
  const isCustom = [choice.allowCustomInput, choice.requiresCustomInput].some((value) => value === true);
  return isCustom ? String(choice.key ?? '').trim() : '';
}

export function firstTruthy(...values) {
  return values.find(Boolean);
}

export function firstDefined(...values) {
  return values.find((value) => value !== undefined && value !== null);
}

export function resolveCurrentChooserPage(hasComposerChooser, chooserPages, normalizedChooserPage) {
  if (!hasComposerChooser) return null;
  return firstTruthy(chooserPages[normalizedChooserPage - 1], chooserPages[0]) ?? null;
}

export function resolveChooserTotalPages(totalPages, chooserPages) {
  const numericTotal = Number(totalPages);
  return Number.isFinite(numericTotal)
    ? Math.max(1, numericTotal)
    : Math.max(1, chooserPages.length);
}

export function choiceKey(choice) {
  return String(Object.assign({}, choice).key ?? '');
}

export function resolveChooserAnswer(selectedChoice, chooserCustomInput) {
  const choice = Object.assign({}, selectedChoice);
  const usesCustomInput = [choice.allowCustomInput, choice.requiresCustomInput]
    .some((value) => value === true);
  const answerSource = usesCustomInput
    ? chooserCustomInput
    : firstTruthy(choice.answerValue, choice.text);
  return String(answerSource ?? '').trim();
}

export function chooserPageAnswer(page, selectedChoice, customInput) {
  return page?.inputKind === 'free_text'
    ? customInput.trim()
    : resolveChooserAnswer(selectedChoice, customInput);
}

export function resolveChoiceAction(selectedChoice) {
  const action = Object.assign({}, selectedChoice).action;
  return action && typeof action === 'object' ? action : null;
}

export function focusElement(ref) {
  const element = Object.assign({}, ref).current;
  if (typeof element?.focus === 'function') {
    element.focus();
  }
}

export function resolveChooserNavigationDirection(event) {
  const isNext = [
    event.key === 'ArrowDown',
    [event.key === 'Tab', event.shiftKey !== true].every(Boolean),
  ].some(Boolean);
  if (isNext) return 1;
  const isPrevious = [
    event.key === 'ArrowUp',
    [event.key === 'Tab', event.shiftKey === true].every(Boolean),
  ].some(Boolean);
  return isPrevious ? -1 : 0;
}

export function matchesHotkey(hotkey, event) {
  const entry = Object.assign({}, hotkey);
  return typeof entry.match === 'function' && entry.match(event) === true;
}

export function preventConfiguredDefault(hotkey, event) {
  if (Object.assign({}, hotkey).preventDefault === true) {
    event.preventDefault();
  }
}

export function canSubmitChooser(requiresCustomInput, chooserCustomInput, effectiveChooserSelectedKey) {
  return requiresCustomInput
    ? Boolean(String(chooserCustomInput ?? '').trim())
    : Boolean(effectiveChooserSelectedKey);
}

export function resolveChooserPageState({ chooser, chooserPages, hasComposerChooser, normalizedChooserPage, chooserSelections }) {
  const currentChooserPage = resolveCurrentChooserPage(
    hasComposerChooser,
    chooserPages,
    normalizedChooserPage,
  );
  const currentPage = Object.assign({}, currentChooserPage);
  const currentPageKey = String(normalizedChooserPage);
  const chooserOptions = Array.isArray(currentPage.options) ? currentPage.options : [];
  const selectedKeys = Array.isArray(chooserSelections[currentPageKey])
    ? chooserSelections[currentPageKey]
    : [];
  return {
    chooserOptions,
    chooserTotalPages: resolveChooserTotalPages(chooser.totalPages, chooserPages),
    currentChooserPage,
    currentPageKey,
    currentSelectionMode: String(currentPage.selectionMode || 'single'),
    selectedKeys,
  };
}

export function resolveChooserSelection({ chooser, chooserOptions, selectedKeys, chooserSelectedKey, singleCustomInputChoiceKey }) {
  const chooserRecommendedKey = String(chooser.recommendedOptionKey ?? '').trim();
  const allowsImplicitOptionFallback = chooser.kind !== 'action_input';
  const fallbackOptionKey = choiceKey(chooserOptions[0]).trim();
  const effectiveChooserSelectedKey = String(firstTruthy(
    selectedKeys[0],
    chooserSelectedKey,
    singleCustomInputChoiceKey,
  ) ?? '').trim();
  return {
    allowsImplicitOptionFallback,
    chooserRecommendedKey,
    effectiveChooserSelectedKey,
    fallbackOptionKey,
    submissionFallbackChoice: allowsImplicitOptionFallback ? chooserOptions[0] : null,
  };
}
